import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { endOfDay, endOfMonth, endOfWeek, format, startOfDay, startOfMonth, startOfWeek } from 'date-fns'
import { ko } from 'date-fns/locale'
import { api } from '../lib/api'
import { formatDateTimeMinute } from '../lib/date'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'
import { useToast } from '../ui/ToastProvider'
import WorkCalendarRbc from './WorkCalendarRbc'
import CalendarEventEditor from './CalendarEventEditor'
import CalendarFiltersPanel from './CalendarFiltersPanel'
import Loading from '../ui/Loading'

const TEAM_CALENDAR_VIEW_ROLES = [
    Roles.OPERATOR,
    Roles.GA_MASTER_ADMIN,
    Roles.GA_MASTER_DEPUTY_ADMIN,
    Roles.GA_LEADER,
    Roles.GA_DEPUTY_LEADER,
    Roles.GA_OTHER_ADMIN,
    Roles.GA_BRANCH_MANAGER,
    Roles.GA_DEPUTY_BRANCH_MANAGER,
    Roles.GA_MANAGER,
    Roles.INSURER_MASTER_ADMIN,
    Roles.INSURER_MASTER_DEPUTY_ADMIN,
    Roles.INSURER_LEADER,
    Roles.INSURER_DEPUTY_LEADER,
    Roles.INSURER_OTHER_ADMIN,
    Roles.INSURER_BRANCH_MANAGER,
    Roles.INSURER_DEPUTY_BRANCH_MANAGER,
    Roles.INSURER_MANAGER,
]

const LS_SHOW_CUSTOMER_CONTACT_EVENTS = 'sgez.calendar.showCustomerContactEvents'
const LS_SHOW_MATURITY_EVENTS = 'sgez.calendar.showMaturityEvents'
const LS_SHOW_CLAIM_EVENTS = 'sgez.calendar.showClaimEvents'
const LS_HIDDEN_HOST_ORGS = 'sgez.calendar.hiddenHostOrgIds'

function unwrap(res) {
    const d = res?.data
    if (d && d.data !== undefined) return d.data
    return d
}

function isCustomerContactEvent(ev) {
    return String(ev?.title || '').trim().startsWith('[연락]')
}

function isMaturityEvent(ev) {
    return String(ev?.title || '').trim().startsWith('[만기고객안내]')
}

function isClaimEvent(ev) {
    return ev?.calendarSourceKind === 'CLAIM_DAILY'
}

function visibleMonthRangeISO(date) {
    const d = date instanceof Date ? date : new Date(date)
    if (!Number.isFinite(d.getTime())) {
        const n = new Date()
        return visibleMonthRangeISO(n)
    }
    const sm = startOfMonth(d)
    const em = endOfMonth(d)
    const from = startOfWeek(sm, { weekStartsOn: 0 })
    const to = endOfWeek(em, { weekStartsOn: 0 })
    return { from: from.toISOString(), to: to.toISOString() }
}

function occurrenceOriginalFromResource(ev) {
    if (!ev?.occurrenceKey || typeof ev.occurrenceKey !== 'string') return null
    const k = ev.occurrenceKey
    const pipe = k.indexOf('|')
    if (pipe < 0) return null
    return k.slice(pipe + 1)
}

function buildPatchPayload(detail, start, end, sourceEvent) {
    const useDef = detail.useDefaultReminders !== false
    let reminderOffsetsMinutes = null
    if (!useDef) {
        let arr = []
        try {
            if (detail.reminderOffsetsJson) arr = JSON.parse(detail.reminderOffsetsJson)
        } catch {
            arr = []
        }
        reminderOffsetsMinutes = Array.isArray(arr) && arr.length ? arr.map(Number) : []
    }
    const base = {
        title: detail.title,
        description: detail.description ?? null,
        startAt: start.toISOString(),
        endAt: end.toISOString(),
        allDay: !!detail.allDay,
        timeZone: detail.timeZone || 'Asia/Seoul',
        useDefaultReminders: useDef,
        reminderOffsetsMinutes,
    }
    const orig = occurrenceOriginalFromResource(sourceEvent)
    const rruleForOcc = (detail?.rrule && String(detail.rrule).trim()) || ''
    if (orig && rruleForOcc) {
        base.occurrenceOriginalStart = orig
    }
    if (detail.eventKind === 'ORG') {
        const orgTargets = Array.isArray(detail.orgTargets)
            ? detail.orgTargets.map((t) => ({
                orgId: Number(t.orgId),
                includeDescendants: !!t.includeDescendants,
            }))
            : []
        const explicitUserIds = Array.isArray(detail.explicitUserIds) ? detail.explicitUserIds.map(Number) : []
        return { ...base, orgTargets, explicitUserIds }
    }
    const share = {
        personalCategory: detail.personalCategory != null && String(detail.personalCategory).trim()
            ? String(detail.personalCategory).trim()
            : '',
        hideDetailsForOrgAdmins: !!detail.hideDetailsForOrgAdmins,
        busyShareOrgId: detail.busyShareOrgId != null ? Number(detail.busyShareOrgId) : null,
    }
    return { ...base, orgTargets: null, explicitUserIds: null, ...share }
}

/**
 * 대시보드 전용: 일정관리와 동일 API·캘린더 컴포넌트, 월 보기 고정.
 * 슬롯 선택·편집 가능 일정은 공용 편집 모달로 처리하고, 그 외 상세는 일정관리로 연결합니다.
 */
export default function DashboardCalendarPanel() {
    const { user, hasAnyRole, refreshUser } = useAuth()
    const { show } = useToast()
    const qc = useQueryClient()
    const navigate = useNavigate()
    const editorRef = useRef(null)
    const [calDate, setCalDate] = useState(() => new Date())
    const viewRange = useMemo(() => visibleMonthRangeISO(calDate), [calDate])

    const [includeCancelled, setIncludeCancelled] = useState(false)
    const [showCustomerContactEvents, setShowCustomerContactEvents] = useState(() => {
        try {
            return localStorage.getItem(LS_SHOW_CUSTOMER_CONTACT_EVENTS) !== 'false'
        } catch {
            return true
        }
    })
    const [showMaturityEvents, setShowMaturityEvents] = useState(() => {
        try {
            return localStorage.getItem(LS_SHOW_MATURITY_EVENTS) !== 'false'
        } catch {
            return true
        }
    })
    const [showClaimEvents, setShowClaimEvents] = useState(() => {
        try {
            return localStorage.getItem(LS_SHOW_CLAIM_EVENTS) !== 'false'
        } catch {
            return true
        }
    })

    useEffect(() => {
        try {
            localStorage.setItem(LS_SHOW_CUSTOMER_CONTACT_EVENTS, showCustomerContactEvents ? 'true' : 'false')
        } catch {
            /* ignore */
        }
    }, [showCustomerContactEvents])
    useEffect(() => {
        try {
            localStorage.setItem(LS_SHOW_MATURITY_EVENTS, showMaturityEvents ? 'true' : 'false')
        } catch {
            /* ignore */
        }
    }, [showMaturityEvents])
    useEffect(() => {
        try {
            localStorage.setItem(LS_SHOW_CLAIM_EVENTS, showClaimEvents ? 'true' : 'false')
        } catch {
            /* ignore */
        }
    }, [showClaimEvents])
    const [hiddenHostOrgIds, setHiddenHostOrgIds] = useState(() => new Set())
    const [teamVisibleOrgIds, setTeamVisibleOrgIds] = useState(() => new Set())
    const [filtersExpanded, setFiltersExpanded] = useState(false)

    const canUseTeamCalendarView = hasAnyRole(TEAM_CALENDAR_VIEW_ROLES) && user?.orgType !== 'PERSONAL'

    const uiPrefsQuery = useQuery({
        queryKey: ['calendar', 'ui-preferences'],
        queryFn: async () => {
            const res = await api.get('/calendar/ui-preferences')
            const data = unwrap(res)
            let ids = Array.isArray(data?.hiddenHostOrgIds) ? data.hiddenHostOrgIds.map(Number).filter(Boolean) : []
            let teamIds = Array.isArray(data?.teamCalendarVisibleOrgIds)
                ? data.teamCalendarVisibleOrgIds.map(Number).filter(Boolean)
                : []
            if (ids.length === 0) {
                try {
                    const raw = localStorage.getItem(LS_HIDDEN_HOST_ORGS)
                    if (raw) {
                        const parsed = JSON.parse(raw)
                        if (Array.isArray(parsed) && parsed.length) {
                            ids = parsed.map(Number).filter(Boolean)
                            localStorage.removeItem(LS_HIDDEN_HOST_ORGS)
                            await api.put('/calendar/ui-preferences', {
                                hiddenHostOrgIds: ids,
                                teamCalendarVisibleOrgIds: teamIds,
                            })
                        }
                    }
                } catch {
                    /* ignore */
                }
            }
            return { hiddenHostOrgIds: ids, teamCalendarVisibleOrgIds: teamIds }
        },
        staleTime: 60_000,
    })

    useEffect(() => {
        if (!uiPrefsQuery.data) return
        setHiddenHostOrgIds(new Set(uiPrefsQuery.data.hiddenHostOrgIds || []))
        setTeamVisibleOrgIds(new Set(uiPrefsQuery.data.teamCalendarVisibleOrgIds || []))
    }, [uiPrefsQuery.data])

    const saveUiPrefsMutation = useMutation({
        mutationFn: async ({ hiddenHostOrgIds: hidden, teamCalendarVisibleOrgIds: team }) => {
            await api.put('/calendar/ui-preferences', {
                hiddenHostOrgIds: hidden,
                teamCalendarVisibleOrgIds: team,
            })
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['calendar', 'ui-preferences'] })
            qc.invalidateQueries({ queryKey: ['calendar-team-events'] })
        },
    })

    const digestPrefsMutation = useMutation({
        mutationFn: async (partial) => {
            await api.put('/users/me', partial)
        },
        onSuccess: async () => {
            show('표시 설정을 저장했습니다. 이후 캘린더와 요약 일정에 반영됩니다.', 'success')
            if (typeof refreshUser === 'function') {
                await refreshUser()
            }
            await qc.invalidateQueries({ queryKey: ['calendar-events'] })
            await qc.invalidateQueries({ queryKey: ['calendar-team-events'] })
        },
        onError: (e) => {
            const msg = e?.response?.data?.message || e?.message || '저장에 실패했습니다.'
            show(msg, 'error')
        },
    })

    const showInsDigest = user?.calendarShowInsuranceAgeDigest !== false
    const showBirthDigest = user?.calendarShowBirthdayDigest !== false
    const showAnnivDigest = user?.calendarShowAnniversaryDigest !== false

    const eventsQuery = useQuery({
        queryKey: ['calendar-events', viewRange.from, viewRange.to, includeCancelled, showInsDigest, showBirthDigest, showAnnivDigest],
        queryFn: async () => {
            const res = await api.get('/calendar/events', {
                params: { from: viewRange.from, to: viewRange.to, includeCancelled },
            })
            const data = unwrap(res)
            return Array.isArray(data) ? data : []
        },
    })

    const teamOrgIdsParam = useMemo(
        () => [...teamVisibleOrgIds].filter((n) => n > 0).sort((a, b) => a - b),
        [teamVisibleOrgIds],
    )

    const teamEventsQuery = useQuery({
        queryKey: ['calendar-team-events', viewRange.from, viewRange.to, teamOrgIdsParam.join(',')],
        queryFn: async () => {
            const res = await api.get('/calendar/team-members/events', {
                params: { from: viewRange.from, to: viewRange.to, orgIds: teamOrgIdsParam },
            })
            const data = unwrap(res)
            return Array.isArray(data) ? data : []
        },
        enabled: canUseTeamCalendarView && teamOrgIdsParam.length > 0,
        staleTime: 30_000,
    })

    const orgOptionsQuery = useQuery({
        queryKey: ['orgs-options', 'mine'],
        queryFn: async () => {
            const res = await api.get('/orgs/options', { params: { scope: 'mine' } })
            const data = unwrap(res)
            return Array.isArray(data) ? data : []
        },
    })

    const rawEvents = useMemo(() => {
        const rawEventsBase = eventsQuery.data || []
        const teamEventsRaw = teamEventsQuery.data || []
        const keys = new Set(
            rawEventsBase.map((e) => (e.occurrenceKey ? String(e.occurrenceKey) : `${e.id}|${e.startAt}`)),
        )
        const extra = teamEventsRaw.filter(
            (e) => !keys.has(e.occurrenceKey ? String(e.occurrenceKey) : `${e.id}|${e.startAt}`),
        )
        return [...rawEventsBase, ...extra]
    }, [eventsQuery.data, teamEventsQuery.data])

    const hostOrgOptions = useMemo(() => {
        const m = new Map()
        for (const ev of rawEvents) {
            if (ev.hostOrgId != null && ev.hostOrgName) {
                m.set(Number(ev.hostOrgId), String(ev.hostOrgName))
            }
        }
        return [...m.entries()].map(([id, name]) => ({ id, name }))
    }, [rawEvents])

    function toggleHiddenHost(orgId) {
        setHiddenHostOrgIds((prev) => {
            const next = new Set(prev)
            if (next.has(orgId)) next.delete(orgId)
            else next.add(orgId)
            saveUiPrefsMutation.mutate({
                hiddenHostOrgIds: [...next],
                teamCalendarVisibleOrgIds: [...teamVisibleOrgIds],
            })
            return next
        })
    }

    const teamCalendarOrgChoices = useMemo(() => {
        const rows = orgOptionsQuery.data || []
        if (user?.roles?.includes?.(Roles.OPERATOR)) return rows
        const oid = Number(user?.orgId)
        if (!oid) return []
        return rows.filter((o) => Number(o.id) === oid)
    }, [orgOptionsQuery.data, user?.orgId, user?.roles])

    const teamCalendarOrgChoicesResolved = useMemo(() => {
        if (teamCalendarOrgChoices.length) return teamCalendarOrgChoices
        const oid = Number(user?.orgId)
        if (!oid || !canUseTeamCalendarView) return []
        return [{ id: oid, name: user?.orgName || `조직 #${oid}` }]
    }, [teamCalendarOrgChoices, user?.orgId, user?.orgName, canUseTeamCalendarView])

    function toggleTeamVisibleOrg(orgId) {
        setTeamVisibleOrgIds((prev) => {
            const next = new Set(prev)
            if (next.has(orgId)) next.delete(orgId)
            else next.add(orgId)
            saveUiPrefsMutation.mutate({
                hiddenHostOrgIds: [...hiddenHostOrgIds],
                teamCalendarVisibleOrgIds: [...next],
            })
            return next
        })
    }

    const visibleEvents = useMemo(() => {
        return rawEvents.filter((ev) => {
            if (!showCustomerContactEvents && isCustomerContactEvent(ev)) return false
            if (!showMaturityEvents && isMaturityEvent(ev)) return false
            if (!showClaimEvents && isClaimEvent(ev)) return false
            if (ev.hostOrgId == null) return true
            return !hiddenHostOrgIds.has(Number(ev.hostOrgId))
        })
    }, [rawEvents, hiddenHostOrgIds, showCustomerContactEvents, showMaturityEvents, showClaimEvents])

    const patchScheduleMut = useMutation({
        mutationFn: async ({ id, start, end, sourceEvent }) => {
            const res = await api.get(`/calendar/events/${id}`)
            const detail = unwrap(res)
            const payload = buildPatchPayload(detail, start, end, sourceEvent)
            const patchRes = await api.patch(`/calendar/events/${id}`, payload)
            return unwrap(patchRes)
        },
        onSuccess: () => {
            show('일정 시간을 변경했습니다', 'success')
            qc.invalidateQueries({ queryKey: ['calendar-events'] })
            qc.invalidateQueries({ queryKey: ['calendar-team-events'] })
        },
        onError: (e) => {
            const msg = e?.response?.data?.message || e?.message || '시간 변경에 실패했습니다'
            show(msg, 'error')
        },
    })

    const onCommitCalendarRange = useCallback(
        async ({ event, start, end }) => {
            if (!event?.id) return
            if (!end || !(end > start)) {
                show('종료 시각이 시작보다 빨라 변경할 수 없습니다', 'error')
                return
            }
            await patchScheduleMut.mutateAsync({ id: event.id, start, end, sourceEvent: event })
        },
        [patchScheduleMut, show],
    )

    const goFullCalendar = useCallback(
        (state) => {
            navigate('/work/calendar', state ? { state } : undefined)
        },
        [navigate],
    )

    const canEditEvent = useCallback((ev) => {
        if (!user || !ev || ev.cancelled) return false
        if (ev.calendarSourceKind === 'CLAIM_DAILY') return false
        const uid = Number(user.id)
        if (Number(ev.createdByUserId) === uid) return true
        if (ev.eventKind === 'PERSONAL' && Number(ev.ownerUserId) === uid) return true
        return false
    }, [user])

    const { briefingToday, briefingThisWeek, weekRangeLabel } = useMemo(() => {
        const now = new Date()
        const dayStart = startOfDay(now).getTime()
        const dayEnd = endOfDay(now).getTime()
        const wkStart = startOfWeek(now, { weekStartsOn: 0 })
        const wkEnd = endOfWeek(now, { weekStartsOn: 0 })
        const weekStart = wkStart.getTime()
        const weekEnd = wkEnd.getTime()

        const sorted = [...visibleEvents].sort((a, b) => String(a.startAt).localeCompare(String(b.startAt)))
        const today = []
        const weekNotToday = []
        for (const ev of sorted) {
            const t = new Date(ev.startAt).getTime()
            if (!Number.isFinite(t)) continue
            if (t >= dayStart && t <= dayEnd) {
                today.push(ev)
            } else if (t >= weekStart && t <= weekEnd) {
                weekNotToday.push(ev)
            }
        }

        const weekRangeLabel = `${format(wkStart, 'M/d(EEE)', { locale: ko })} ~ ${format(wkEnd, 'M/d(EEE)', { locale: ko })}`

        return {
            briefingToday: today.slice(0, 16),
            briefingThisWeek: weekNotToday.slice(0, 24),
            weekRangeLabel,
        }
    }, [visibleEvents])

    const handleSelectEvent = useCallback(
        (ev) => {
            if (ev?.id == null) return
            if (ev.calendarSourceKind === 'CLAIM_DAILY') {
                navigate('/support/claims/applications')
                return
            }
            if (canEditEvent(ev)) {
                editorRef.current?.openEditFromEventSummary(ev)
                return
            }
            goFullCalendar({ openEventId: Number(ev.id) })
        },
        [canEditEvent, goFullCalendar, navigate],
    )

    const handleSelectSlot = useCallback(
        (slotInfo) => {
            if (!slotInfo?.start) return
            editorRef.current?.openPersonalAt(slotInfo.start, slotInfo.end)
        },
        [],
    )

    if (eventsQuery.isLoading) {
        return (
            <div className="flex min-h-[280px] items-center justify-center rounded-lg border border-slate-200 bg-white">
                <Loading />
            </div>
        )
    }

    return (
        <div className="space-y-3">
            <CalendarFiltersPanel
                panelIdPrefix="dashboard-calendar"
                filtersExpanded={filtersExpanded}
                onFiltersExpandedChange={setFiltersExpanded}
                showInsDigest={showInsDigest}
                showBirthDigest={showBirthDigest}
                showAnnivDigest={showAnnivDigest}
                digestPrefsMutation={digestPrefsMutation}
                includeCancelled={includeCancelled}
                onIncludeCancelledChange={setIncludeCancelled}
                showCustomerContactEvents={showCustomerContactEvents}
                onShowCustomerContactEventsChange={setShowCustomerContactEvents}
                showMaturityEvents={showMaturityEvents}
                onShowMaturityEventsChange={setShowMaturityEvents}
                showClaimEvents={showClaimEvents}
                onShowClaimEventsChange={setShowClaimEvents}
                hostOrgOptions={hostOrgOptions}
                hiddenHostOrgIds={hiddenHostOrgIds}
                onToggleHiddenHost={toggleHiddenHost}
                canUseTeamCalendarView={canUseTeamCalendarView}
                teamCalendarOrgChoicesResolved={teamCalendarOrgChoicesResolved}
                teamVisibleOrgIds={teamVisibleOrgIds}
                onToggleTeamVisibleOrg={toggleTeamVisibleOrg}
            />

            <div className="flex flex-col gap-4 xl:flex-row xl:items-stretch">
                <div className="min-w-0 flex-1">
                    <WorkCalendarRbc
                        compact
                        views={['month']}
                        events={visibleEvents}
                        view="month"
                        date={calDate}
                        onNavigate={setCalDate}
                        onView={() => { }}
                        onSelectEvent={handleSelectEvent}
                        canEditEvent={canEditEvent}
                        onCommitRange={onCommitCalendarRange}
                        onSelectSlot={handleSelectSlot}
                    />
                </div>
                <aside className="min-w-0 shrink-0 xl:w-[min(100%,20rem)] flex flex-col gap-2">
                    <div className="border-b border-slate-200 pb-1.5">
                        <h3 className="text-sm font-semibold text-slate-800">주요 일정 브리핑</h3>
                        <p className="mt-0.5 text-[11px] text-slate-500 leading-snug">오늘과 이번 주(일~토) 일정을 나눠 보여 줍니다.</p>
                    </div>
                    <div className="rounded border border-slate-200 bg-white overflow-y-auto max-h-[min(400px,45vh)] xl:max-h-[min(680px,calc(100vh-200px))] divide-y divide-slate-100">
                        <section>
                            <div className="sticky top-0 z-[1] border-b border-slate-100 bg-slate-50/95 px-3 py-2 backdrop-blur-sm">
                                <h4 className="text-xs font-semibold text-slate-800">오늘</h4>
                                <p className="text-[10px] text-slate-500 mt-0.5">
                                    {format(new Date(), 'M월 d일 (EEE)', { locale: ko })}
                                </p>
                            </div>
                            {briefingToday.length === 0 ? (
                                <div className="px-3 py-3 text-xs text-slate-500">오늘 표시할 일정이 없습니다.</div>
                            ) : (
                                <ul className="divide-y divide-slate-100">
                                    {briefingToday.map((ev) => (
                                        <li key={ev.occurrenceKey || String(ev.id)}>
                                            <button
                                                type="button"
                                                className="flex w-full flex-col gap-0.5 px-3 py-2 text-left text-xs hover:bg-slate-50"
                                                onClick={() => handleSelectEvent(ev)}
                                            >
                                                <span className="font-medium text-slate-900 line-clamp-2 leading-snug">
                                                    {ev.cancelled ? <span className="text-rose-600">[취소] </span> : null}
                                                    {ev.title}
                                                </span>
                                                <span className="text-[10px] text-slate-500 leading-snug">
                                                    {formatDateTimeMinute(ev.startAt)}
                                                    {ev.calendarSourceKind === 'CLAIM_DAILY'
                                                        ? ` · 청구 ${ev.claimCount || 0}건`
                                                        : ev.eventKind === 'ORG'
                                                            ? ' · 조직'
                                                            : ' · 개인'}
                                                </span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>
                        <section>
                            <div className="sticky top-0 z-[1] border-b border-slate-100 bg-indigo-50/90 px-3 py-2 backdrop-blur-sm">
                                <h4 className="text-xs font-semibold text-indigo-950">이번 주</h4>
                                <p className="text-[10px] text-slate-600 mt-0.5 leading-snug">
                                    {weekRangeLabel}
                                    <span className="text-slate-400"> · 오늘 제외</span>
                                </p>
                            </div>
                            {briefingThisWeek.length === 0 ? (
                                <div className="px-3 py-3 text-xs text-slate-500">이번 주(오늘 제외)에 표시할 일정이 없습니다.</div>
                            ) : (
                                <ul className="divide-y divide-slate-100">
                                    {briefingThisWeek.map((ev) => (
                                        <li key={ev.occurrenceKey || String(ev.id)}>
                                            <button
                                                type="button"
                                                className="flex w-full flex-col gap-0.5 px-3 py-2 text-left text-xs hover:bg-slate-50"
                                                onClick={() => handleSelectEvent(ev)}
                                            >
                                                <span className="font-medium text-slate-900 line-clamp-2 leading-snug">
                                                    {ev.cancelled ? <span className="text-rose-600">[취소] </span> : null}
                                                    {ev.title}
                                                </span>
                                                <span className="text-[10px] text-slate-500 leading-snug">
                                                    {formatDateTimeMinute(ev.startAt)}
                                                    {ev.calendarSourceKind === 'CLAIM_DAILY'
                                                        ? ` · 청구 ${ev.claimCount || 0}건`
                                                        : ev.eventKind === 'ORG'
                                                            ? ' · 조직'
                                                            : ' · 개인'}
                                                </span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>
                    </div>
                </aside>
            </div>
            <CalendarEventEditor ref={editorRef} />
        </div>
    )
}
